/**
 * 일일대여(daily) v2 — web 병합/가격 헬퍼 (Phase 3, docs/DAILY_V2_PLAN.md)
 *
 * 타임슬롯 v2가 [이용 시간 옵션 × 세부 상품] 2축 매트릭스인 것과 달리, 일일대여 v2는
 * **세부 상품마다 독립적인 기간 티어**(product_sub_product_day_prices)를 갖는다(결정 2).
 * 따라서 축은 하나(세부 상품)이고, 각 세부 상품이 자기 `min_days` 티어 배열을 소유한다.
 *
 * ⚠️ 단가 수식은 **기존 `getDailyRateForDays`를 그대로 재사용**한다(소스만 v1
 * `product_pricing` 행 → v2 티어 행으로 교체). 수식을 복제하면 web/서버검증/Edge
 * 3계층이 갈라져 CART_PRICE_MISMATCH가 난다.
 *
 * 시즌(성수기)은 09-21부터 daily v2에도 적용된다(SEASON_V2_CONTRACT.md — rate 행이
 * `sub_display_order` 축을 가짐). 시즌 계산은 pricing.ts의
 * `getSeasonalDailyV2Rates`/`calculateSeasonalDailyV2Price`가 담당하고, 이 파일은
 * 티어 소스(day_prices → DailyTier[])와 단가 수식 재사용만 맡는다.
 */

import { getDailyRateForDays, toDailyPricingRows } from "./pricing";

export { toDailyPricingRows };
import {
  pickLangRowsByDisplayOrder,
  type SubProductRpcRow,
} from "./timeslot-matrix";

/** get_product_detail_full 의 `day_prices` 행 (언어 무관 1벌) */
export interface DayPriceRpcRow {
  sub_display_order: number;
  min_days: number;
  /** numeric은 드라이버에 따라 string일 수 있음 */
  price_per_day: number | string;
}

/** 세부 상품 1개의 기간 티어 1행 */
export interface DailyTier {
  minDays: number;
  pricePerDay: number;
}

/**
 * 유효 단가 규칙 — **이 파일의 단일 규약: `> 0` 만 가격이다.**
 *
 * 0원(또는 음수·NaN) 티어는 데이터 위반이며 서버·Edge 재가격이 fail-closed로
 * 거부한다(checkout.ts `v2DailyRate <= 0` → "Daily pricing not available",
 * cart.ts `getDailyV2RateForDays(...) <= 0` → unavailable).
 *
 * 그래서 **표시·대표가 경로는 0원 티어를 "없는 셈 치고 다음 티어로 넘어가면 안 된다"**.
 * 넘어가면 카드·요금 상세가 결제 불가능한 가격을 광고하게 되고(BookingCard는
 * 같은 화면에서 그 세부 상품을 판매 불가로 감춘다), 고객이 본 가격과 결제 결과가
 * 갈라진다. 대표가·기준가·요금 상세가 전부 이 한 함수만 본다.
 */
export function isValidDailyPrice(price: number): boolean {
  return Number.isFinite(price) && price > 0;
}

/**
 * 기준 티어 = `min_days = 1` 티어, 없으면 가장 짧은 구간 (결정 6).
 * 0원이어도 **다음 티어로 넘어가지 않는다** — 위 규칙에 따라 그 세부 상품은
 * "가격 없음(판매 불가)"이 정답이기 때문이다.
 */
function pickBaseTier(tiers: DailyTier[]): DailyTier | undefined {
  const exact = tiers.find((t) => t.minDays === 1);
  if (exact) return exact;
  return [...tiers].sort((a, b) => a.minDays - b.minDays)[0];
}

export interface MergedDailySubProduct {
  id: string;
  name: string;
  description: string;
  /** 세부 상품별 주문당 최대 구매 수량 — null = 무제한 (결정 10) */
  maxPurchaseQuantity: number | null;
  /** 언어 무관 정체성 키 — 예약 스냅샷(sub_display_order)·재가격 매칭 */
  displayOrder: number;
  /** minDays 오름차순 정렬된 기간 티어 (빈 배열 = 판매 불가) */
  tiers: DailyTier[];
}

/**
 * 선택한 세부 상품의 티어 배열 — `product.day_prices`(언어 무관 1벌)에서 sub 좌표로
 * 걸러 minDays 오름차순. 파싱 규칙은 `mergeDailyMatrixConfig`와 동일(숫자 파싱
 * 실패 행만 제외, 0원 행은 그대로 — 선택 집합을 깎으면 3계층 단가가 갈라진다).
 * 클라이언트 시즌 재가격(checkout util·useBookingCart·CartItemsList)의 티어 소스.
 */
export function getDailyV2TiersForSub(
  dayPriceRows:
    | Array<{
        sub_display_order?: number | null;
        min_days: number | string;
        price_per_day: number | string;
      }>
    | null
    | undefined,
  subDisplayOrder: number | null | undefined,
): DailyTier[] {
  if (!dayPriceRows || subDisplayOrder == null) return [];
  const tiers: DailyTier[] = [];
  for (const row of dayPriceRows) {
    if (row.sub_display_order !== subDisplayOrder) continue;
    const minDays = Number(row.min_days);
    const price = Number(row.price_per_day);
    if (!Number.isFinite(minDays) || !Number.isFinite(price)) continue;
    tiers.push({ minDays, pricePerDay: price });
  }
  return tiers.sort((a, b) => a.minDays - b.minDays);
}

/**
 * daily v2 적용 단가 — 대여일수에 해당하는 구간의 일 단가.
 * 규칙은 v1과 완전히 동일(가장 높은 `min_days` 중 `days >= min_days`인 구간).
 * 티어가 없거나 0원이면 0 → 호출측이 fail-closed 처리한다.
 */
export function getDailyV2RateForDays(days: number, tiers: DailyTier[]): number {
  if (!tiers || tiers.length === 0) return 0;
  return getDailyRateForDays(days, toDailyPricingRows(tiers));
}

/**
 * 1일 기준가 — 리스트박스에서 **기간 선택 전** 표시하는 단가(결정 6).
 * `min_days = 1` 티어가 정상 데이터지만, 없으면 최소 `min_days` 티어로 폴백한다
 * (가격을 잃는 것보다 "가장 짧은 구간"을 보여주는 편이 안전).
 *
 * 기준 티어가 0원이면 0을 돌려준다(`isValidDailyPrice` 규칙) — 호출측
 * (BookingCard `dailySellableSubs`)이 그 세부 상품을 판매 불가로 처리한다.
 */
export function getDailyV2BaseRate(tiers: DailyTier[]): number {
  if (!tiers || tiers.length === 0) return 0;
  const price = Number(pickBaseTier(tiers)?.pricePerDay);
  return isValidDailyPrice(price) ? price : 0;
}

/**
 * 카드·검색·JSON-LD 대표가 폴백 (P1-0) — day-prices의 **1일 기준가 최소값**.
 *
 * 결정 6: "최저 구간 단가"가 아니라 1일 기준가다. 장기 할인 구간가를 대표가로
 * 쓰면 카드에서 본 가격보다 실제 결제가가 항상 비싸진다.
 * 세부 상품별로 1일 기준가(`getDailyV2BaseRate`와 같은 규칙)를 구해 그 최소값을 반환.
 *
 * ⚠️ 0원 행은 **행 단위로 걸러내지 않는다**(구 동작). 걸러내면 `{1일: 0, 3일: 12000}`
 * 같은 위반 데이터에서 3일 할인가가 대표가로 승격되어, BookingCard가 판매 불가로
 * 감춘 세부 상품의 가격을 카드·JSON-LD가 광고하게 된다. 기준가가 유효하지 않은
 * 세부 상품은 대표가 자체가 없다(`isValidDailyPrice` 단일 규칙).
 */
export function getDailyV2MinPrice(
  dayPriceRows?: Array<{
    sub_display_order?: number | null;
    min_days: number;
    price_per_day: number | string;
  }> | null,
): number | null {
  if (!dayPriceRows || dayPriceRows.length === 0) return null;
  // sub 좌표별 최소 min_days 행 = 기준 티어 (좌표가 없는 데이터는 단일 그룹 취급)
  const baseBySub = new Map<number, { minDays: number; price: number }>();
  for (const row of dayPriceRows) {
    const minDays = Number(row.min_days);
    if (!Number.isFinite(minDays)) continue;
    const price = Number(row.price_per_day);
    const sub = row.sub_display_order ?? 0;
    const current = baseBySub.get(sub);
    if (
      !current ||
      minDays < current.minDays ||
      (minDays === current.minDays && price < current.price)
    ) {
      baseBySub.set(sub, { minDays, price });
    }
  }
  let min = Infinity;
  for (const { price } of baseBySub.values()) {
    if (!isValidDailyPrice(price)) continue; // 기준가 위반 = 그 세부 상품은 가격 없음
    if (price < min) min = price;
  }
  return min === Infinity ? null : min;
}

/**
 * 언어 병합 — 세부 상품 행(전 언어) + 티어 행(언어 무관)을 한 언어의 구조로 재조립.
 * 세부 상품 언어 폴백 규칙은 타임슬롯 v2와 **같은 함수**를 공유한다.
 *
 * ⚠️ `tiers`에는 0원 행도 **그대로 담는다**(숫자 파싱 실패 행만 제외). 이 배열은
 * 구간 선택(`getDailyV2RateForDays`)의 입력이고, 서버(cart.ts·checkout.ts)도
 * 0원 행을 남긴 채 같은 선택을 한 뒤 `<= 0`이면 거부한다. 여기서만 0원 행을 빼면
 * 클라이언트가 한 단계 아래 티어를 고르게 되어 3계층 단가가 갈라진다
 * (CART_PRICE_MISMATCH). "0원은 가격이 아니다"(`isValidDailyPrice`) 규칙은
 * **선택된 단가·표시 단가**에서 적용된다 — 선택 집합을 깎는 방식이 아니다.
 */
export function mergeDailyMatrixConfig(
  rows: {
    subProductRows: SubProductRpcRow[];
    dayPriceRows: DayPriceRpcRow[];
  },
  lang: string,
  originalLang: string,
): { subProducts: MergedDailySubProduct[] } {
  const tiersBySub = new Map<number, DailyTier[]>();
  for (const row of rows.dayPriceRows || []) {
    const price = Number(row.price_per_day);
    if (!Number.isFinite(price)) continue;
    const list = tiersBySub.get(row.sub_display_order) ?? [];
    list.push({ minDays: Number(row.min_days), pricePerDay: price });
    tiersBySub.set(row.sub_display_order, list);
  }
  for (const list of tiersBySub.values()) {
    list.sort((a, b) => a.minDays - b.minDays);
  }

  const subProducts = pickLangRowsByDisplayOrder(
    rows.subProductRows || [],
    lang,
    originalLang,
  )
    .sort((a, b) => a.display_order - b.display_order)
    .map((row, index) => ({
      id: row.id || `subp-${row.display_order ?? index}`,
      name: row.name || "",
      description: row.description || "",
      maxPurchaseQuantity: row.max_purchase_quantity ?? null,
      displayOrder: row.display_order ?? index,
      tiers: tiersBySub.get(row.display_order) ?? [],
    }));

  return { subProducts };
}

/**
 * ⚠️ `composeDailyV2ListPrice`(리스트 행 가격 표기)는 08-20 제거됐다 —
 * BookingCard 리스트 행에서 가격 표시 자체가 사라졌기 때문이다
 * (`docs/preview-bookingcard-price-4modes.html`). 표시가 필요해지면
 * `getDailyV2RateForDays`/`getDailyV2BaseRate`를 그대로 쓰면 된다 — 단가 소스를
 * 새로 만들면 표시가와 담기 단가가 갈라진다.
 */

/** PricingInfo daily v2 요금 상세 — 세부 상품 그룹 1행 */
export interface DailyV2PriceGroupRow {
  key: string;
  /** 라벨 포맷(`{days}일 이상`)은 i18n을 쥔 컴포넌트가 담당 */
  minDays: number;
  price: number;
}

export interface DailyV2PriceGroup {
  id: string;
  /**
   * 그룹 헤더(세부상품명) — null = 헤더 생략:
   * 노출 그룹이 1개(전환 직후 = v1 모양)이거나, 이름이 공백/상품명과 동일할 때.
   * (타임슬롯 v2 `buildTimeslotV2PriceGroups`와 동일 규칙)
   */
  name: string | null;
  rows: DailyV2PriceGroupRow[];
}

/**
 * PricingInfo daily v2 요금 상세 — 세부 상품별 그룹 리스트.
 * 타임슬롯 v2 그룹 규칙 미러(>0 행만, 빈 그룹 제외, 헤더 생략 규칙 동일).
 *
 * 그룹 노출 판정은 BookingCard의 `dailySellableSubs`와 **같은 규칙**
 * (`getDailyV2BaseRate > 0`)이다 — 기준가가 위반이면 요금 상세도 그 세부 상품을
 * 통째로 감춘다. 아니면 예약 리스트에 없는 상품의 요금표가 상세에만 남는다.
 */
export function buildDailyV2PriceGroups(
  subProducts: Array<{ id: string; name: string; tiers: DailyTier[] }>,
  productName?: string,
): DailyV2PriceGroup[] {
  const groups = subProducts
    .map((sp) => {
      const tiers = sp.tiers ?? [];
      const sellable = getDailyV2BaseRate(tiers) > 0;
      return {
        id: sp.id,
        rawName: (sp.name ?? "").trim(),
        rows: sellable
          ? [...tiers]
              .filter((tier) => isValidDailyPrice(tier.pricePerDay))
              .sort((a, b) => a.minDays - b.minDays)
              .map((tier) => ({
                key: `${sp.id}-${tier.minDays}`,
                minDays: tier.minDays,
                price: tier.pricePerDay,
              }))
          : [],
      };
    })
    .filter((g) => g.rows.length > 0);
  const single = groups.length === 1;
  const product = productName?.trim();
  return groups.map(({ id, rawName, rows }) => ({
    id,
    name:
      single || !rawName || (product != null && rawName === product)
        ? null
        : rawName,
    rows,
  }));
}
